import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getTimeLineDetail,
  getShowImageUrl,
  updateSucculentPlant,
  updateTimeLineNode,
  deleteSucculentPlant,
  deleteTimelineNode,
  deleteFlowerImage,
  getMySucculentList,
  getSucculentPlantById,
  type TimeLineNode,
  type SucculentPlant,
} from '../utils/flowerApi';
import { ImagePreview } from '../components/ImagePreview';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast } from '../components/Toast';
import { DatePicker } from '../components/DatePicker';
import { isLogin } from '../utils/auth';
import { ImageUpload } from '../components/ImageUpload';
import styles from './SucculentDetail.module.css';

/**
 * 多肉详情页 — 左右布局时间线（参考老项目 y-time-line-item）
 */
export const SucculentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as { plant?: SucculentPlant; source?: 'public' | 'mine' } | null) || {};
  const navPlant = navState.plant;
  const navSource = navState.source;

  // 检查是否为只读模式
  const [isReadOnly, setIsReadOnly] = useState(false);

  useEffect(() => {
    if (!isLogin()) {
      setIsReadOnly(true);
      return;
    }
    if (navSource === 'public') {
      setIsReadOnly(true);
      return;
    }
    if (!id) return;
    getMySucculentList(1, 200).then(({ items }) => {
      const isMine = items.some((p) => p.succulent_plant_id === id);
      setIsReadOnly(!isMine);
    }).catch(() => {
      setIsReadOnly(true);
    });
  }, [id, navSource]);

  const [plant, setPlant] = useState<SucculentPlant | null>(navPlant || null);
  const [timeline, setTimeline] = useState<TimeLineNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 如果 navPlant 不可用（页面刷新后丢失），从 API 获取植物信息
  useEffect(() => {
    if (navPlant || !id) return;
    getSucculentPlantById(id).then((p) => {
      if (p) setPlant(p);
    });
  }, [id, navPlant]);

  // 编辑状态
  const [editingInfo, setEditingInfo] = useState(false);
  const [editForm, setEditForm] = useState<Partial<SucculentPlant>>({});
  const [editingNode, setEditingNode] = useState<string | null>(null);
  const [nodeForm, setNodeForm] = useState<{ labels: string; notes: string }>({ labels: '', notes: '' });

  // 预览弹窗
  const [preview, setPreview] = useState<{ imageUrl: string } | null>(null);

  // 上传弹窗
  const [showUpload, setShowUpload] = useState(false);

  // 折叠状态（手机端默认折叠含图片的节点）
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // 照片管理模式
  const [isManageMode, setIsManageMode] = useState(false);

  // 对话框状态
  const [pendingDialog, setPendingDialog] = useState<{
    type: 'deletePlant' | 'deleteNode' | 'deleteImage';
    nodeId?: string;
    imageId?: string;
  } | null>(null);

  // Toast 提示
  const [toast, setToast] = useState<string | null>(null);

  // ---- Fetch Data ----
  const fetchData = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const nodes = await getTimeLineDetail(id);
      setTimeline(nodes);
      // 手机端默认折叠超过2张图片的节点
      if (window.innerWidth <= 768) {
        setCollapsedNodes(new Set(
          nodes.filter((n) => n.images.length > 2).map((n) => n.time_line_node_id)
        ));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 窗口大小变化时重新初始化折叠状态
  useEffect(() => {
    if (timeline.length === 0) return;
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      setCollapsedNodes((prev) => {
        const hasAny = prev.size > 0;
        if (isMobile && !hasAny) return new Set(timeline.filter((n) => n.images.length > 2).map((n) => n.time_line_node_id));
        if (!isMobile && hasAny) return new Set();
        return prev;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timeline]);

  // ---- Preview ----
  const openPreview = useCallback(async (node: TimeLineNode, imageIndex: number) => {
    const imageUrl = await getShowImageUrl(node.images[imageIndex].flower_image_id);
    setPreview({ imageUrl });
  }, []);

  // ---- Edit Plant Info ----
  const startEditInfo = () => {
    if (!plant) return;
    setEditForm({
      plant_name: plant.plant_name,
      variety: plant.variety || '',
      acquisition_date: plant.acquisition_date || '',
      price: plant.price || '',
      notes: plant.notes || '',
    });
    setEditingInfo(true);
  };

  const savePlantInfo = async () => {
    if (!plant) return;
    try {
      const updated = await updateSucculentPlant({
        succulent_plant_id: plant.succulent_plant_id,
        plant_name: editForm.plant_name || undefined,
        variety: editForm.variety || undefined,
        acquisition_date: editForm.acquisition_date || undefined,
        price: editForm.price || undefined,
        notes: editForm.notes || undefined,
      });
      setPlant(updated);
      setEditingInfo(false);
    } catch (err) {
      setToast(err instanceof Error ? err.message : '保存失败');
    }
  };

  // ---- Edit Node ----
  const startEditNode = (node: TimeLineNode) => {
    setEditingNode(node.time_line_node_id);
    setNodeForm({
      labels: node.labels.join(', '),
      notes: node.notes || '',
    });
  };

  const saveNode = async (nodeId: string) => {
    try {
      const labels = nodeForm.labels
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean);
      await updateTimeLineNode({
        time_line_id: nodeId,
        labels,
        notes: nodeForm.notes || undefined,
      });
      setEditingNode(null);
      fetchData();
    } catch (err) {
      setToast(err instanceof Error ? err.message : '保存失败');
    }
  };

  // ---- Delete ----
  const handleDeletePlant = async () => {
    if (!plant) return;
    setPendingDialog({ type: 'deletePlant' });
  };

  const handleDeleteNode = (nodeId: string) => {
    setPendingDialog({ type: 'deleteNode', nodeId });
  };

  const handleDeleteImage = (imageId: string) => {
    setPendingDialog({ type: 'deleteImage', imageId });
  };

  const confirmDeletePlant = async () => {
    if (!plant) return;
    try {
      await deleteSucculentPlant(plant.succulent_plant_id);
      navigate('/');
    } catch (err) {
      setToast(err instanceof Error ? err.message : '删除失败');
    }
  };

  const confirmDeleteNode = async () => {
    if (!pendingDialog?.nodeId) return;
    try {
      await deleteTimelineNode(pendingDialog.nodeId);
      fetchData();
    } catch (err) {
      setToast(err instanceof Error ? err.message : '删除失败');
    }
  };

  const confirmDeleteImage = async () => {
    if (!pendingDialog?.imageId) return;
    try {
      await deleteFlowerImage(pendingDialog.imageId);
      fetchData();
    } catch (err) {
      setToast(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleUploadSuccess = () => {
    fetchData(true);
  };

  // ---- Toggle collapsed state for timeline nodes ----
  const toggleCollapse = useCallback((nodeId: string) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
      return next;
    });
  }, []);

  // ---- Loading / Error ----
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorPage}>
        <p>{error}</p>
        <button onClick={() => navigate('/')}>返回首页</button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <header className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          &#8592;
        </button>
        <h1 className={styles.topTitle}>
          {plant?.plant_name || '加载中...'}
        </h1>
        <div className={styles.topActions}>
          {!isReadOnly && (
            <button className={styles.topDeleteBtn} onClick={handleDeletePlant} title="删除植物">&#128465;</button>
          )}
        </div>
      </header>

      <div className={styles.content}>
        {/* Left: Info panel */}
        <aside className={styles.sidebar}>
          <div className={styles.infoCard}>
            <div className={styles.infoHeader}>
              <h3>基本信息</h3>
              {!isReadOnly && !editingInfo && (
                <button className={styles.editBtn} onClick={startEditInfo}>编辑</button>
              )}
            </div>

            {editingInfo ? (
              <div className={styles.editForm}>
                <label>名称<input value={editForm.plant_name || ''} onChange={(e) => setEditForm((f) => ({ ...f, plant_name: e.target.value }))} /></label>
                <label>品种<input value={editForm.variety || ''} onChange={(e) => setEditForm((f) => ({ ...f, variety: e.target.value }))} /></label>
                <label className={styles.dateLabel}>
                  <span className={styles.fieldLabel}>获取日期</span>
                  <DatePicker
                    value={editForm.acquisition_date || ''}
                    onChange={(v) => setEditForm((f) => ({ ...f, acquisition_date: v }))}
                  />
                </label>
                <label>价格<input value={editForm.price || ''} onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))} /></label>
                <label>备注<textarea value={editForm.notes || ''} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} rows={3} /></label>
                <div className={styles.formActions}>
                  <button onClick={() => setEditingInfo(false)}>取消</button>
                  <button className={styles.saveBtn} onClick={savePlantInfo}>保存</button>
                </div>
              </div>
            ) : (
              <div className={styles.infoFields}>
                <InfoRow label="品种" value={plant?.variety} />
                <InfoRow label="获取日期" value={plant?.acquisition_date} />
                <InfoRow label="价格" value={plant?.price} />
                <InfoRow label="备注" value={plant?.notes} />
              </div>
            )}
          </div>

          {!isReadOnly && (
            <div className={styles.sidebarActions}>
              <button className={styles.sidebarUpload} onClick={() => setShowUpload(true)}>
                + 添加照片
              </button>
              <button
                className={`${styles.sidebarManage} ${isManageMode ? styles.active : ''}`}
                onClick={() => setIsManageMode((v) => !v)}
              >
                {isManageMode ? '完成' : '管理照片'}
              </button>
            </div>
          )}
        </aside>

        {/* Right: Timeline */}
        <main className={styles.timeline}>
          {timeline.length === 0 ? (
            isReadOnly ? (
              <div className={styles.emptyTimeline}>
                <p>暂无时间线数据</p>
              </div>
            ) : (
              <div className={styles.emptyTimeline}>
                <p>暂无时间线数据</p>
                <p>点击上方「添加照片」开始记录</p>
              </div>
            )
          ) : (
            timeline.map((node) => (
              <div key={node.time_line_node_id} className={styles.node}>
                {/* Left: Date + dot + line */}
                <div className={styles.dateColumn}>
                  <span className={styles.date}>{formatDateShort(node.show_time)}</span>
                  <div className={styles.dot} />
                  {node !== timeline[timeline.length - 1] && <div className={styles.line} />}
                </div>

                {/* Right: Content */}
                <div className={styles.nodeContent}>
                  {/* Notes */}
                  {node.notes && !editingNode && <p className={styles.nodeText}>{node.notes}</p>}

                  {editingNode === node.time_line_node_id ? (
                    <div className={styles.nodeEdit}>
                      <input
                        className={styles.labelInput}
                        placeholder="标签，用逗号分隔"
                        value={nodeForm.labels}
                        onChange={(e) => setNodeForm((f) => ({ ...f, labels: e.target.value }))}
                      />
                      <input
                        className={styles.noteInput}
                        placeholder="备注"
                        value={nodeForm.notes}
                        onChange={(e) => setNodeForm((f) => ({ ...f, notes: e.target.value }))}
                      />
                      <div className={styles.nodeEditActions}>
                        <button onClick={() => saveNode(node.time_line_node_id)}>保存</button>
                        <button onClick={() => setEditingNode(null)}>取消</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Labels + relative time */}
                      {(node.labels.length > 0 || node.notes) && (
                        <div className={styles.nodeMeta}>
                          {node.labels.map((label) => (
                            <span key={label} className={styles.label}>
                              {label}
                            </span>
                          ))}
                          <span className={styles.relativeTime}>{formatRelativeTime(node.show_time)}</span>
                          {!isReadOnly && (
                            <div className={styles.nodeActions}>
                              <button onClick={() => startEditNode(node)} title="编辑">&#9998;</button>
                              <button className={styles.deleteNodeBtn} onClick={() => handleDeleteNode(node.time_line_node_id)} title="删除节点">&#128465;</button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Image grid — first 2 always shown, rest collapsible on mobile */}
                  {node.images.length > 0 && (
                    <div className={styles.imageGrid}>
                      {/* Always show first 2 images */}
                      {node.images.slice(0, 2).map((img, imgIndex) => (
                        <div
                          key={img.flower_image_id}
                          className={`${styles.imageCell} ${isManageMode ? styles.showDelete : ''}`}
                        >
                          <img
                            src={img.show_image_url}
                            alt=""
                            onClick={() => openPreview(node, imgIndex)}
                          />
                          {!isReadOnly && (
                            <button
                              className={styles.deleteImageBtn}
                              onClick={() => handleDeleteImage(img.flower_image_id)}
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      ))}
                      {/* Extra images: toggle on mobile */}
                      {node.images.length > 2 && (
                        <>
                          {/* 剩余图片 — 展开时显示 */}
                          {!collapsedNodes.has(node.time_line_node_id) && (
                            node.images.slice(2).map((img, imgIndex) => (
                              <div
                                key={img.flower_image_id}
                                className={`${styles.imageCell} ${isManageMode ? styles.showDelete : ''}`}
                              >
                                <img
                                  src={img.show_image_url}
                                  alt=""
                                  onClick={() => openPreview(node, imgIndex + 2)}
                                />
                                {!isReadOnly && (
                                  <button
                                    className={styles.deleteImageBtn}
                                    onClick={() => handleDeleteImage(img.flower_image_id)}
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            ))
                          )}
                          {/* 折叠/展开控制条 — 放在图片下方 */}
                          <div className={styles.imageToggle} onClick={() => toggleCollapse(node.time_line_node_id)}>
                            <span className={styles.imageCount}>
                              {collapsedNodes.has(node.time_line_node_id)
                                ? `+${node.images.length - 2}张照片`
                                : '收起'}
                            </span>
                            <span className={collapsedNodes.has(node.time_line_node_id) ? styles.iconDown : styles.iconUp}>
                              {collapsedNodes.has(node.time_line_node_id) ? '▼' : '▲'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </main>
      </div>

      {/* Image Preview */}
      {preview && (
        <ImagePreview
          imageUrl={preview.imageUrl}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Upload Modal */}
      {showUpload && id && (
        <ImageUpload
          succulentPlantId={id}
          onSuccess={handleUploadSuccess}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Confirm Dialogs */}
      {pendingDialog?.type === 'deletePlant' && plant && (
        <ConfirmDialog
          title="删除确认"
          message={`确定删除「${plant.plant_name}」及其所有图片？`}
          confirmText="删除"
          danger
          onConfirm={confirmDeletePlant}
          onCancel={() => setPendingDialog(null)}
        />
      )}
      {pendingDialog?.type === 'deleteNode' && (
        <ConfirmDialog
          title="删除确认"
          message="确定删除此时间线节点及其所有图片？"
          confirmText="删除"
          danger
          onConfirm={confirmDeleteNode}
          onCancel={() => setPendingDialog(null)}
        />
      )}
      {pendingDialog?.type === 'deleteImage' && (
        <ConfirmDialog
          title="删除确认"
          message="确定删除此图片？"
          confirmText="删除"
          danger
          onConfirm={confirmDeleteImage}
          onCancel={() => setPendingDialog(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast message={toast} onClose={() => setToast(null)} />
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <span>ICP备案号:</span>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
          闽ICP备2025102433号-1
        </a>
      </footer>
    </div>
  );
};

// ---- Sub-components ----

const InfoRow: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value}</span>
    </div>
  );
};

/**
 * 短日期格式: "25年11月9"
 */
function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr);
  const y = date.getFullYear() % 100;
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${y}年${m}月${d}`;
}

/**
 * 相对时间: "6个月前", "6个月零1天前"
 */
function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const target = new Date(dateStr);
  const diffMs = now.getTime() - target.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return '今天';
  if (diffDays === 1) return '1天前';
  if (diffDays < 30) return `${diffDays}天前`;

  const months = Math.floor(diffDays / 30);
  const remainingDays = diffDays % 30;

  if (months < 12) {
    if (remainingDays === 0) return `${months}个月前`;
    return `${months}个月零${remainingDays}天前`;
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years}年前`;
  return `${years}年零${remainingMonths}个月前`;
}
